import React, { useState, useRef, useEffect } from "react";
import TextInput from "../components/formComponents/TextAreaInput";
import EmailInput from "../components/formComponents/EmailInput";
import SelectInput from "../components/formComponents/SelectInput";
import TextAreaInput from "../components/formComponents/TextAreaInput";
import emailjs from "emailjs-com";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/JoinUs.css";

const JoinUs = () => {
  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    nationality: "",
    degree: "",
    references: "",
    researchExperience: "",
    cvLink: "",
    academicTranscriptsLink: "",
    supervisor: "",
    researchProject: "",
  });
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [userCaptchaInput, setUserCaptchaInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formRef = useRef();

  // Generate a random CAPTCHA question
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    setCaptchaQuestion(`${num1} + ${num2}`);
    setCaptchaAnswer((num1 + num2).toString());
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleClearForm = () => {
    setFormData({
      email: "",
      fullName: "",
      nationality: "",
      degree: "",
      references: "",
      researchExperience: "",
      cvLink: "",
      academicTranscriptsLink: "",
      supervisor: "",
      researchProject: "",
    });
    setUserCaptchaInput("");
    generateCaptcha(); // Reset CAPTCHA
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate CAPTCHA
    if (userCaptchaInput !== captchaAnswer) {
      toast.error("Incorrect CAPTCHA. Please try again.");
      return;
    }

    setIsLoading(true);

    try {
      await emailjs.sendForm(
        "service_fnlndk4",
        "template_d2vpgwu",
        formRef.current,
        "wwAsS8H0uMp4ENYif"
      );
      setIsSubmitted(true);
      toast.success("Application submitted successfully!");
      handleClearForm();
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="join-us-container">
      <div className="page-header">
        <h1>Joining the Lab</h1>
      </div>

      <div className="instructions">
        <p>
          Hello and welcome! On this page, you will find information on how to
          submit an application to become an official member of the GAIC lab.
          Please read everything on this page to ensure that you are applying
          for the correct degree, and that the work we focus on is something
          that interests you.
        </p>

        <h2>Preliminaries</h2>
        <p>
          Before applying to the lab, please make sure that you meet the
          university’s entry requirements for your prospective degree. More
          information about these requirements can be found on the official{" "}
          <a
            href="https://www.wits.ac.za/course-finder/postgraduate/humanities/master-of-arts/#anchor4"
            target="_blank"
            rel="noopener noreferrer"
            className="external-link"
          >
            Wits website
          </a>
          . Please note that our application process is only for research-based
          degrees. You must also apply to the department for an MA program; only
          after departmental acceptance will the GAIC Lab confirm your
          acceptance.
        </p>

        <h2>Applying to the Lab</h2>
        <p>
          We are a South African research lab passionate about producing
          world-class research and advancing the state of Artificial
          Intelligence and Culture both locally and abroad. We publish at top
          venues worldwide. Learn more about our{" "}
          <a
            href="https://gaiclab.co.za/"
            target="_blank"
            rel="noopener noreferrer"
            className="external-link"
          >
            vision and mission
          </a>{" "}
          and our{" "}
          <a
            href="https://gaiclab.co.za/projects"
            target="_blank"
            rel="noopener noreferrer"
            className="external-link"
          >
            current projects
          </a>
          .
        </p>

        <p>
          Interested? Check out some of our{" "}
          <a
            href="https://gaiclab.co.za/publications"
            target="_blank"
            rel="noopener noreferrer"
            className="external-link"
          >
            published works
          </a>{" "}
          and think about what excites you. What would you like to explore,
          change, or improve? Let us know!
        </p>

        <h2>Procedure and Deadlines</h2>
        <ul>
          <li>
            <strong>Application closing date:</strong> 15 November 2026
          </li>
          <li>
            <strong>Shortlist notification date:</strong> 30 November 2026
          </li>
          <li>
            <strong>Final notification date:</strong> 7 December 2026
          </li>
        </ul>

        <p>
          If successful, we’ll guide you through registering with the
          university. Otherwise, explore other options through the{" "}
          <a
            href="https://www.wits.ac.za/wsoa/digital-arts/staff/"
            target="_blank"
            rel="noopener noreferrer"
            className="external-link"
          >
            School/Department
          </a>
          .
        </p>
      </div>

      <div className="application-form">
        <h2>Application Form</h2>
        {isSubmitted ? (
          <div className="submission-success">
            <h2>Thank you for your application!</h2>
            <p>We will review your submission and get back to you shortly.</p>
            <button
              className="submit-button"
              onClick={() => setIsSubmitted(false)}
            >
              Submit Another Application
            </button>
          </div>
        ) : (
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="form-group">
              <EmailInput
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <TextInput
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <TextInput
                label="Nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <SelectInput
                label="Degree"
                name="degree"
                value={formData.degree}
                onChange={handleChange}
                required
                options={[
                  { value: "Honours", label: "Honours" },
                  { value: "MA (research)", label: "MA (research)" },
                  { value: "PhD", label: "PhD" },
                ]}
              />
            </div>
            <div className="form-group">
              <TextAreaInput
                label="Research Experience"
                name="researchExperience"
                value={formData.researchExperience}
                onChange={handleChange}
                required
                placeholder="Describe your research experience."
              />
            </div>
            <div className="form-group">
              <TextInput
                label="CV (Google Drive link)"
                name="cvLink"
                value={formData.cvLink}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <TextInput
                label="Academic Transcripts (Google Drive link)"
                name="academicTranscriptsLink"
                value={formData.academicTranscriptsLink}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <SelectInput
                label="Supervisor"
                name="supervisor"
                value={formData.supervisor}
                onChange={handleChange}
                required
                options={[
                  { value: "Oluwarotimi Randle", label: "Oluwarotimi Randle" },
                  { value: "Andre Gopal", label: "Andre Gopal" },
                ]}
              />
            </div>
            <div className="form-group">
              <TextAreaInput
                label="Proposed Research Project"
                name="researchProject"
                value={formData.researchProject}
                onChange={handleChange}
                required
              />
            </div>

            {/* CAPTCHA Section */}
            <div className="form-group">
              <label htmlFor="captcha">
                CAPTCHA: What is {captchaQuestion}?
              </label>
              <input
                type="text"
                id="captcha"
                placeholder="Enter your answer"
                value={userCaptchaInput}
                onChange={(e) => setUserCaptchaInput(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        )}
      </div>
      <ToastContainer />
    </section>
  );
};

export default JoinUs;
